import { accountRowOrder, formatAccountRowLabel } from './accountTableRowOrder';
import { isDatePeriod } from './weekNumberUtils';

/**
 * Determines if a period index represents a past period relative to current date
 * @param periodIndex - The 0-based index of the period in the fiscal year
 * @param periodNames - Array of period names (could be month names, dates, or week numbers)
 * @param fiscalStartMonth - The fiscal year start month (1-12, where 1=January)
 * @param selectedYear - The start year of the forecast
 * @param aggregationPeriod - The aggregation period type (monthly, weekly, quarterly)
 * @returns true if the period is in the past, false otherwise
 */
export const isPeriodInPast = (
    periodIndex: number,
    periodNames: string[] | undefined,
    fiscalStartMonth: number,
    selectedYear: number,
    aggregationPeriod: string = 'monthly'
): boolean => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth() + 1; // 1-12
    
    if (aggregationPeriod === 'weekly' && periodNames && periodNames[periodIndex]) {
        const periodName = periodNames[periodIndex];
        const periodDate = parsePeriodDate(periodName);
        if (periodDate) {
            const today = getTodayInUkCalendar();

            const nextPeriodDate = periodNames
                .slice(periodIndex + 1)
                .map(parsePeriodDate)
                .find((date): date is Date => date !== null);

            if (nextPeriodDate) {
                return nextPeriodDate <= today;
            }

            const endOfWeek = new Date(periodDate);
            endOfWeek.setDate(endOfWeek.getDate() + 7);
            return endOfWeek <= today;
        }
    }
    
    if (aggregationPeriod === 'monthly') {
        const periodCalendarMonth = ((fiscalStartMonth - 1 + periodIndex) % 12) + 1;
        
        const periodCalendarYear = fiscalStartMonth + periodIndex > 12 
            ? selectedYear + 1 
            : selectedYear;
        
        if (periodCalendarYear < currentYear) {
            return true;
        } else if (periodCalendarYear === currentYear) {
            return periodCalendarMonth < currentMonth;
        }
    }
    
    if (aggregationPeriod === 'quarterly') {
        const quarterStartMonthOffset = periodIndex * 3;
        const quarterEndMonthOffset = quarterStartMonthOffset + 2; // Last month of the quarter
        
        const quarterEndMonth = ((fiscalStartMonth - 1 + quarterEndMonthOffset) % 12) + 1;
        const quarterEndYear = fiscalStartMonth + quarterEndMonthOffset > 12 
            ? selectedYear + Math.floor((fiscalStartMonth - 1 + quarterEndMonthOffset) / 12)
            : selectedYear;
        
        if (quarterEndYear < currentYear) {
            return true;
        } else if (quarterEndYear === currentYear) {
            return quarterEndMonth < currentMonth;
        }
    }
    
    return false;
};

/**
 * Determines if a forecast is in the past based on year difference and current month
 * @param selectedYear - The start year of the forecast
 * @param currentYear - The current calendar year  
 * @param currentMonth - Current fiscal month (0-based, always 0-11 due to modulo 12 calculation)
 * @param totalPeriods - Total number of periods in the fiscal year
 * @param fiscalStartMonth - The fiscal year start month (1-12, where 1=January)
 * @param currentCalendarMonth - The current calendar month (1-12, where 1=January)
 * @returns true if the forecast is in the past, false otherwise
 */
export const isForecastInPast = (
    selectedYear: number | undefined,
    currentYear: number | undefined,
    currentMonth: number | undefined,
    totalPeriods: number,
    fiscalStartMonth?: number,
    currentCalendarMonth?: number
): boolean => {
    if (selectedYear === undefined || currentYear === undefined) return false;
    const yearDiff = currentYear - selectedYear;
    
    if (yearDiff > 1)
        return true;
    
    if (yearDiff === 1) {
        if (currentMonth === undefined) return false;
        
        if (fiscalStartMonth !== undefined && currentCalendarMonth !== undefined) {            
            const fiscalEndMonth = fiscalStartMonth === 1 ? 12 : fiscalStartMonth - 1;
            
            if (fiscalStartMonth === 1) {
                return true;
            } else {
                return currentCalendarMonth >= fiscalStartMonth;
            }
        }

        return true;
    }
    
    return false;
};

const parsePeriodDate = (periodName: string): Date | null => {
    if (!/^\d{2}-\d{2}-\d{2,4}$/.test(periodName)) {
        return null;
    }

    const [day, month, year] = periodName.split('-').map(Number);
    const fullYear = year < 100 ? 2000 + year : year;
    const parsedDate = new Date(fullYear, month - 1, day);

    if (Number.isNaN(parsedDate.getTime())) {
        return null;
    }

    parsedDate.setHours(0, 0, 0, 0);
    return parsedDate;
};

const getTodayInUkCalendar = (): Date => {
    try {
        const formatter = new Intl.DateTimeFormat('en-GB', {
            timeZone: 'Europe/London',
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
        const dateParts = formatter.formatToParts(new Date());
        const day = Number(dateParts.find(part => part.type === 'day')?.value);
        const month = Number(dateParts.find(part => part.type === 'month')?.value);
        const year = Number(dateParts.find(part => part.type === 'year')?.value);

        if (Number.isFinite(day) && Number.isFinite(month) && Number.isFinite(year)) {
            return new Date(year, month - 1, day);
        }
    } catch {
        // Fall back to the browser's local calendar if timezone support is unavailable.
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
};

export const resolveCurrentPeriodIndex = (
    currentPeriod: number | undefined,
    aggregationPeriod: string = 'monthly',
    periodNames?: string[]
): number => {
    if (currentPeriod === undefined || currentPeriod === null) {
        return 0;
    }

    if (aggregationPeriod === 'quarterly') {
        return Math.floor(currentPeriod / 3);
    }

    if (aggregationPeriod !== 'weekly' || !Array.isArray(periodNames)) {
        return currentPeriod;
    }

    const today = getTodayInUkCalendar();

    const datedPeriods = periodNames
        .map((periodName, index) => ({ index, date: parsePeriodDate(periodName) }))
        .filter((period): period is { index: number; date: Date } => period.date !== null);

    if (datedPeriods.length === 0) {
        return currentPeriod;
    }

    const currentWeeklyPeriod = [...datedPeriods].reverse().find(period => period.date <= today);
    if (currentWeeklyPeriod) {
        return currentWeeklyPeriod.index;
    }

    return datedPeriods[0].date > today ? 0 : currentPeriod;
};

/**
 * Calculates Year-to-Go (YTG) sum from current or next period to end of year
 * @param dataArray - Array of period data values (monthly, quarterly, weekly, etc.)
 * @param currentMonth - Current period index (0-based)
 * @param selectedYear - The year of the forecast data being viewed (optional)
 * @param currentYear - The current calendar year (optional)
 * @param aggregationPeriod - Aggregation period type (optional)
 * @param fiscalStartMonth - The fiscal year start month 1-12 (optional)
 * @param currentCalendarMonth - The current calendar month 1-12 (optional, defaults to current date)
 * @param includeCurrentPeriod - Whether to include current period in the YTG sum
 * @returns Sum of values from current/next period to end of year
 */
export const calculateYTG = (
    dataArray: number[] | undefined,
    currentMonth: number | undefined,
    selectedYear?: number,
    currentYear?: number,
    aggregationPeriod?: string,
    fiscalStartMonth?: number,
    currentCalendarMonth?: number,
    includeCurrentPeriod: boolean = false,
    periodNames?: string[]
): number => {
    
    if (!dataArray || !Array.isArray(dataArray) || currentMonth === undefined || currentMonth === null) {
        return 0;
    }
    
    const period = aggregationPeriod || 'monthly';
    
    const totalPeriods = period === 'monthly' 
        ? 12 
        : Math.max(0, dataArray.length - 1);
    
    // Convert calendar month to appropriate period index based on aggregation period
    const currentPeriodIndex = resolveCurrentPeriodIndex(currentMonth, period, periodNames);
    
    // Check if forecast year is past/future to handle fiscal years spanning two calendar years
    if (selectedYear !== undefined && currentYear !== undefined) {
        const yearDiff = currentYear - selectedYear;
        
        // Future forecast: return sum of all periods (YTG = FY)
        if (yearDiff < 0) {
            let futureYearSum = 0;
            for (let i = 0; i < totalPeriods; i++) {
                futureYearSum += dataArray[i] || 0;
            }
            return futureYearSum;
        }
        
        // Same calendar year, but check if forecast hasn't started yet
        if (yearDiff === 0 && fiscalStartMonth !== undefined) {
            const calMonth = currentCalendarMonth ?? (new Date().getMonth() + 1);
            if (calMonth < fiscalStartMonth) {
                // We're before the fiscal year starts - return FY
                let futureYearSum = 0;
                for (let i = 0; i < totalPeriods; i++) {
                    futureYearSum += dataArray[i] || 0;
                }
                return futureYearSum;
            }
        }
        
        // Past forecast
        if (yearDiff > 1) {
            return 0;
        } else if (yearDiff === 1) {
            if (fiscalStartMonth !== undefined) {
                const calMonth = currentCalendarMonth ?? (new Date().getMonth() + 1);
                if (isForecastInPast(selectedYear, currentYear, currentMonth, totalPeriods, fiscalStartMonth, calMonth)) {
                    return 0;
                }
            } else {
                return 0;
            }
        }
    }
    
    let ytgSum = 0;
    const summedValues = [];
    const startIndex = includeCurrentPeriod ? currentPeriodIndex : currentPeriodIndex + 1;
    for (let i = startIndex; i < totalPeriods; i++) {
        const value = dataArray[i] || 0;
        ytgSum += value;
        summedValues.push({ index: i, value });
    }
    
    
    return ytgSum;
};

/**
 * Calculates Year-to-Go (YTG) percentage from current/next period to the end of year
 * For percentage fields that should be calculated as a ratio of two underlying values
 * @param numeratorArray - Array of numerator values (e.g., grossProfit)
 * @param denominatorArray - Array of denominator values (e.g., grossRevenue)
 * @param currentMonth - Current month index (0-based)
 * @param selectedYear - The year of the forecast data being viewed (optional)
 * @param currentYear - The current calendar year (optional)
 * @param aggregationPeriod - Aggregation period type (optional)
 * @param fiscalStartMonth - The fiscal year start month 1-12 (optional)
 * @param currentCalendarMonth - The current calendar month 1-12 (optional, defaults to current date)
 * @param includeCurrentPeriod - Whether to include current period in YTG
 * @returns Percentage calculated as (YTG numerator / YTG denominator) * 100, or 0 if denominator is 0
 */
export const calculateYTGPercentage = (
    numeratorArray: number[] | undefined, 
    denominatorArray: number[] | undefined, 
    currentMonth: number | undefined,
    selectedYear?: number,
    currentYear?: number,
    aggregationPeriod?: string,
    fiscalStartMonth?: number,
    currentCalendarMonth?: number,
    includeCurrentPeriod: boolean = false,
    periodNames?: string[]
): number => {
    if (!numeratorArray || !denominatorArray || !Array.isArray(numeratorArray) || !Array.isArray(denominatorArray) || 
        currentMonth === undefined || currentMonth === null) {
        return 0;
    }
    
    const ytgNumerator = calculateYTG(numeratorArray, currentMonth, selectedYear, currentYear, aggregationPeriod, fiscalStartMonth, currentCalendarMonth, includeCurrentPeriod, periodNames);
    const ytgDenominator = calculateYTG(denominatorArray, currentMonth, selectedYear, currentYear, aggregationPeriod, fiscalStartMonth, currentCalendarMonth, includeCurrentPeriod, periodNames);
    
    if (ytgDenominator === 0) return 0;
    
    return Math.round((ytgNumerator / ytgDenominator) * 100 * 100) / 100; // Round to 2 decimal places
};

export const formatValueForClipboard = (value: any): string => {
    if (value === undefined || value === null) return '';
    const roundedValue = Math.round(value * 100) / 100;
    return roundedValue.toString();
};

/**
 * Gets the value to copy for a specific period, preferring actual over forecast when actuals are enabled
 * @param forecastValue - The forecast value
 * @param actualValue - The actual value (may be null/undefined)
 * @param showActuals - Whether actuals mode is enabled
 * @returns The value to copy (actual if available and showActuals is true, otherwise forecast)
 */
export const getValueForCopy = (forecastValue: number | undefined, actualValue: number | null | undefined, showActuals: boolean): number | undefined => {
    if (showActuals && actualValue !== null && actualValue !== undefined) {
        return actualValue;
    }
    return forecastValue;
};

export const isPeriodInActualsWindow = (
    periodIndex: number,
    currentPeriodIndex: number | undefined | null
): boolean => {
    if (currentPeriodIndex === undefined || currentPeriodIndex === null) {
        return false;
    }
    return periodIndex < currentPeriodIndex;
};

/**
 * Creates week number headers for clipboard copy
 * @param periodNames - Array of period names (could include dates)
 * @param aggregationPeriod - The aggregation period type
 * @returns Array of week numbers or empty strings for non-date periods
 */
export const createWeekNumberHeaders = (periodNames: string[], aggregationPeriod?: string): string[] => {
    if (aggregationPeriod !== 'weekly') {
        return [];
    }
    
    let weekCounter = 0;
    return periodNames.map((period) => {
        if (isDatePeriod(period)) {
            weekCounter++;
            return weekCounter.toString();
        }
        return ''; // For YTG, FY, etc.
    });
};

/**
 * Adds week number row to CSV content if weekly aggregation
 * @param csvContent - Current CSV content
 * @param periodNames - Array of period names
 * @param aggregationPeriod - The aggregation period type
 * @param leadingColumns - Number of leading columns before period columns (default 1 for account/summary tables, 2 for demand table)
 * @returns Updated CSV content with week numbers if applicable
 */
export const addWeekNumberRow = (csvContent: string, periodNames: string[], aggregationPeriod?: string, leadingColumns: number = 1): string => {
    if (aggregationPeriod !== 'weekly') {
        return csvContent;
    }
    
    const weekNumbers = createWeekNumberHeaders(periodNames, aggregationPeriod);
    const leadingTabs = '\t'.repeat(leadingColumns);
    return csvContent + leadingTabs + weekNumbers.join('\t') + '\n';
};

export const getOrderedMonthNames = (startMonth: number): string[] => {
    const baseMonthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthNames = [...baseMonthNames];
    const rotationIndex = (startMonth - 1) % 12;
    return [...monthNames.slice(rotationIndex), ...monthNames.slice(0, rotationIndex)];
};

export interface SummaryChannel {
    channelId?: number | null;
    parentChannelId?: number | null;
    path?: string;
    name: string;
    customers?: Array<{
        customerId?: number;
        name: string;
        skus?: Array<{
            productId?: number;
            skuNumber: string;
            description: string;
            [key: string]: any;
        }>;
        [key: string]: any;
    }>;
    children?: SummaryChannel[];
    [key: string]: any;
}

export interface SummaryTableData {
    totals?: { [key: string]: number[] };
    skus?: Array<{
        skuNumber: string;
        description: string;
        customers?: Array<{
            name: string;
            [key: string]: any;
        }>;
        [key: string]: any;
    }>;
    channels?: SummaryChannel[];
    items?: Array<{
        customerName: string;
        skuNumber: string;
        description: string;
        [key: string]: any;
    }>;
    currentMonth?: number;
    periodNames?: string[];
}

type SummaryRowContext = {
    skuNumber?: string;
    channelId?: string;
    customerId?: string;
};

const getSummaryChannelId = (channel: SummaryChannel): string =>
    channel.channelId != null
        ? String(channel.channelId)
        : `path:${channel.path ?? channel.name}`;

const getSummaryCustomerId = (customer: { customerId?: number; name: string }): string =>
    String(customer.customerId ?? customer.name);

const findSummaryChannel = (
    channels: SummaryChannel[] | undefined,
    channelId: string
): SummaryChannel | undefined => {
    for (const channel of channels ?? []) {
        if (getSummaryChannelId(channel) === channelId) {
            return channel;
        }
        const child = findSummaryChannel(channel.children, channelId);
        if (child) {
            return child;
        }
    }
    return undefined;
};

export const copySummaryTableToClipboard = async (
    summaryData: SummaryTableData,
    view: string,
    startMonth: number,
    selectedYear?: number,
    currentYear?: number,
    aggregationPeriod?: string,
    showActuals?: boolean,
    groupBy?: string,
    expandedRows?: Set<string>,
    expandedCustomers?: Set<string>,
    ytgSourceData?: SummaryTableData
): Promise<void> => {
    if (!summaryData || !summaryData.totals) {
        return;
    }

    // Use period names from API response if available, otherwise fall back to month names
    const basePeriodNames = summaryData.periodNames || getOrderedMonthNames(startMonth);

    // Mirror table behavior: only include YTG when the selected forecast is not fully in the past.
    const periodNamesOnly = basePeriodNames.filter((name: string) => name !== 'YTG' && name !== 'FY');
    const effectiveCurrentYear = currentYear ?? new Date().getFullYear();
    const effectiveCurrentCalendarMonth = new Date().getMonth() + 1;
    const totalPeriods = aggregationPeriod === 'monthly' ? 12 : periodNamesOnly.length;
    const includeYTG = !isForecastInPast(
        selectedYear,
        effectiveCurrentYear,
        summaryData.currentMonth,
        totalPeriods,
        startMonth,
        effectiveCurrentCalendarMonth
    );
    const headerNames = includeYTG ? [...periodNamesOnly, 'YTG', 'FY'] : [...periodNamesOnly, 'FY'];
    
    let csvContent = '';
    
    // Add headers based on groupBy mode
    if (groupBy === 'list') {
        csvContent = addWeekNumberRow(csvContent, headerNames, aggregationPeriod, 4);
        csvContent += '\tCustomer\tSKU\tDescription\t' + headerNames.map((name: string) => name.toUpperCase()).join('\t') + '\n';
    } else {
        csvContent = addWeekNumberRow(csvContent, headerNames, aggregationPeriod, 1);
        csvContent += '\t' + headerNames.map((name: string) => name.toUpperCase()).join('\t') + '\n';
    }

    // Calculate number of periods (excluding FY total)
    const totalArray = summaryData.totals?.[view] || [];
    const periodCount = periodNamesOnly.length;
    const fyIndex = totalArray.length > 0 ? totalArray.length - 1 : periodCount;

    // Determine actual key based on view
    const actualKey = view === 'units' ? 'actualUnits' 
        : view === 'grossRev' ? 'actualGrossRev'
        : view === 'triggers' ? 'actualTriggers'
        : view === 'overlay' ? 'actualOverlay'
        : view === 'fixedSpend' ? 'actualFixedSpend'
        : view === 'cogs' ? 'actualCogs'
        : view === 'logs' ? 'actualLogs'
        : view === 'netRev' ? 'actualNetRev'
        : view === 'grossProfit' ? 'actualGrossProfit'
        : view === 'grossProfitPercent' ? 'actualGrossProfitPercent'
        : null;
    const supportsActuals = showActuals && actualKey !== null;
    const getActualSeries = (entity: any) => {
        if (!supportsActuals || !actualKey) {
            return undefined;
        }

        return entity?.[actualKey];
    };
    
    // Get actual array if available
    const totalsActualArray = getActualSeries(summaryData.totals);
    const formatSummaryPeriodValue = (forecastValue: number | undefined, actualValue: number | null | undefined, periodIndex: number): string => {
        if (!showActuals || !actualKey) {
            return formatValueForClipboard(forecastValue);
        }
        if (actualValue !== null && actualValue !== undefined) {
            return formatValueForClipboard(actualValue);
        }
        if (isPeriodInActualsWindow(periodIndex, summaryData.currentMonth)) {
            return '';
        }
        return formatValueForClipboard(forecastValue);
    };
    
    const totalValues = totalArray.slice(0, periodCount).map((value: number, index: number) => {
        const actualValue = totalsActualArray?.[index];
        return formatSummaryPeriodValue(value, actualValue, index);
    });
    const ytgData = ytgSourceData || summaryData;
    const ytgCurrentPeriod = ytgData.currentMonth ?? summaryData.currentMonth;
    const ytgAggregationPeriod = ytgSourceData && aggregationPeriod !== 'weekly' ? 'weekly' : aggregationPeriod;
    const forecastHasNotStarted = selectedYear !== undefined
        && currentYear !== undefined
        && (selectedYear > currentYear || (selectedYear === currentYear && effectiveCurrentCalendarMonth < startMonth));
    const getYtgBoundaryIndex = (seriesLength: number): number => {
        if (forecastHasNotStarted) {
            return 0;
        }

        if (ytgCurrentPeriod === undefined || ytgCurrentPeriod === null || Number.isNaN(ytgCurrentPeriod)) {
            return 0;
        }

        return Math.max(0, Math.min(ytgCurrentPeriod, Math.max(0, seriesLength - 1)));
    };

    const buildBlendedYtgSeries = (
        forecastSeries: Array<number | null | undefined> | undefined,
        actualSeries: Array<number | null | undefined> | undefined
    ): number[] => {
        if (!Array.isArray(forecastSeries)) {
            return [];
        }

        const boundaryIndex = getYtgBoundaryIndex(forecastSeries.length);
        const actualValues = supportsActuals && Array.isArray(actualSeries) ? actualSeries : undefined;

        return forecastSeries.map((forecastValue, index) => {
            if (actualValues && index < boundaryIndex) {
                return actualValues[index] ?? 0;
            }

            if (actualValues && index === boundaryIndex) {
                return actualValues[index] ?? forecastValue ?? 0;
            }

            return forecastValue ?? 0;
        });
    };

    const calculateYtgValue = (series: number[]): number => {
        if (!Array.isArray(series) || series.length === 0 || ytgCurrentPeriod === undefined || ytgCurrentPeriod === null) {
            return 0;
        }

        const totalPeriods = ytgAggregationPeriod === 'monthly'
            ? 12
            : Math.max(0, series.length - 1);
        const startIndex = forecastHasNotStarted ? 0 : getYtgBoundaryIndex(totalPeriods);
        let total = 0;

        for (let i = startIndex; i < totalPeriods; i++) {
            total += series[i] ?? 0;
        }

        return total;
    };

    const calculateGrossProfitPercentYtgValue = (entity: any): number => {
        const grossProfitSeries = buildBlendedYtgSeries(entity?.grossProfit, entity?.actualGrossProfit);
        const netRevSeries = buildBlendedYtgSeries(entity?.netRev, entity?.actualNetRev);
        const grossProfitYtg = calculateYtgValue(grossProfitSeries);
        const netRevYtg = calculateYtgValue(netRevSeries);

        return netRevYtg === 0 ? 0 : (grossProfitYtg / netRevYtg) * 100;
    };

    const getYtgEntity = (entity: any, context: SummaryRowContext = {}): any => {
        if (!ytgSourceData || !entity) {
            return entity;
        }

        if (entity === summaryData.totals) {
            return ytgSourceData.totals || entity;
        }

        if (groupBy === 'sku') {
            if (entity?.skuNumber) {
                return ytgSourceData.skus?.find((sku: any) => sku.skuNumber === entity.skuNumber) || entity;
            }
            if (entity?.name) {
                const parentSku = context.skuNumber
                    ? summaryData.skus?.find((sku: any) => sku.skuNumber === context.skuNumber)
                    : summaryData.skus?.find((sku: any) => sku.customers?.some((customer: any) => customer.name === entity.name));
                const ytgParentSku = parentSku ? ytgSourceData.skus?.find((sku: any) => sku.skuNumber === parentSku.skuNumber) : null;
                return ytgParentSku?.customers?.find((customer: any) => customer.name === entity.name) || entity;
            }
        } else if (groupBy === 'customer') {
            if (entity?.customers && entity?.name) {
                const channelId = context.channelId || getSummaryChannelId(entity);
                return findSummaryChannel(ytgSourceData.channels, channelId) || entity;
            }
            if (entity?.skus && entity?.name) {
                const ytgParentChannel = context.channelId
                    ? findSummaryChannel(ytgSourceData.channels, context.channelId)
                    : undefined;
                const customerId = context.customerId || getSummaryCustomerId(entity);
                return ytgParentChannel?.customers?.find(
                    (customer: any) => getSummaryCustomerId(customer) === customerId
                ) || entity;
            }
            if (entity?.skuNumber) {
                const ytgParentChannel = context.channelId
                    ? findSummaryChannel(ytgSourceData.channels, context.channelId)
                    : undefined;
                const ytgParentCustomer = ytgParentChannel?.customers?.find(
                    (customer: any) => getSummaryCustomerId(customer) === context.customerId
                );
                return ytgParentCustomer?.skus?.find((sku: any) =>
                    (entity.productId != null && sku.productId === entity.productId) ||
                    (entity.productId == null && sku.skuNumber === entity.skuNumber)
                ) || entity;
            }
        } else if (groupBy === 'list') {
            return ytgSourceData.items?.find((item: any) =>
                item.customerName === entity.customerName &&
                item.skuNumber === entity.skuNumber &&
                item.description === entity.description
            ) || entity;
        }

        return entity;
    };

    const totalYtgEntity = getYtgEntity(summaryData.totals);
    const totalYTG = formatValueForClipboard(
        view === 'grossProfitPercent'
                ? calculateGrossProfitPercentYtgValue(totalYtgEntity)
                : calculateYtgValue(buildBlendedYtgSeries(totalYtgEntity?.[view], getActualSeries(totalYtgEntity)))
    );
    const totalFYForecast = totalArray[fyIndex];
    const totalFYActual = totalsActualArray?.[fyIndex];
    const totalFY = formatValueForClipboard(getValueForCopy(totalFYForecast, totalFYActual, showActuals || false));
    
    if (groupBy === 'list') {
        csvContent += 'TOTAL\t\t\t\t' + totalValues.join('\t') + (includeYTG ? '\t' + totalYTG : '') + '\t' + totalFY + '\n';
    } else {
        csvContent += 'TOTAL\t' + totalValues.join('\t') + (includeYTG ? '\t' + totalYTG : '') + '\t' + totalFY + '\n';
    }

    // Helper function to format entity values
    const formatEntityRow = (entity: any, indent: string = '', context: SummaryRowContext = {}) => {
        const entityArray = entity?.[view] || [];
        const entityActualArray = getActualSeries(entity);
        
        const entityValues = entityArray.slice(0, periodCount).map((value: number, index: number) => {
            const actualValue = entityActualArray?.[index];
            return formatSummaryPeriodValue(value, actualValue, index);
        });
        const ytgEntity = getYtgEntity(entity, context);
        const entityYTG = formatValueForClipboard(
            view === 'grossProfitPercent'
                ? calculateGrossProfitPercentYtgValue(ytgEntity)
                : calculateYtgValue(buildBlendedYtgSeries(ytgEntity?.[view], getActualSeries(ytgEntity)))
        );
        const entityFYForecast = entityArray[fyIndex];
        const entityFYActual = entityActualArray?.[fyIndex];
        const entityFY = formatValueForClipboard(getValueForCopy(entityFYForecast, entityFYActual, showActuals || false));

        return indent + entityValues.join('\t') + (includeYTG ? '\t' + entityYTG : '') + '\t' + entityFY;
    };

    // Handle different groupBy modes
    if (groupBy === 'sku') {
        summaryData.skus?.forEach((sku) => {
            const skuLabel = `${sku.skuNumber} - ${sku.description}`;
            csvContent += skuLabel + '\t' + formatEntityRow(sku) + '\n';
            
            // Only include customers if SKU is expanded
            if (expandedRows?.has(sku.skuNumber)) {
                sku.customers?.forEach((customer) => {
                    csvContent += customer.name + '\t' + formatEntityRow(customer, '', { skuNumber: sku.skuNumber }) + '\n';
                });
            }
        });
    } else if (groupBy === 'customer') {
        const appendChannels = (channels: SummaryChannel[] | undefined, depth: number = 0) => {
            channels?.forEach((channel) => {
                const channelId = getSummaryChannelId(channel);
                csvContent += `${channel.name}\t${formatEntityRow(
                    channel,
                    '',
                    { channelId }
                )}\n`;

                if (!expandedRows?.has(channelId) && !expandedRows?.has(channel.name)) {
                    return;
                }

                channel.customers?.forEach((customer) => {
                    const customerId = getSummaryCustomerId(customer);
                    csvContent += `${customer.name}\t${formatEntityRow(
                        customer,
                        '',
                        { channelId, customerId }
                    )}\n`;

                    const customerKey = `${channelId}|${customerId}`;
                    const legacyCustomerKey = `${channel.name}|${customer.name}`;
                    if (expandedCustomers?.has(customerKey) ||
                        expandedCustomers?.has(legacyCustomerKey)) {
                        customer.skus?.forEach((sku) => {
                            const skuLabel = `${sku.skuNumber} - ${sku.description}`;
                            csvContent += `${skuLabel}\t${formatEntityRow(
                                sku,
                                '',
                                { channelId, customerId }
                            )}\n`;
                        });
                    }
                });
                appendChannels(channel.children, depth + 1);
            });
        };
        appendChannels(summaryData.channels);
    } else if (groupBy === 'list') {
        summaryData.items?.forEach((item) => {
            csvContent += '\t' + item.customerName + '\t' + item.skuNumber + '\t' + item.description + '\t' + formatEntityRow(item) + '\n';
        });
    }

    try {
        await navigator.clipboard.writeText(csvContent);
    } catch (err) {
        console.error('Failed to copy: ', err);
    }
};

export interface PromotionFixedSpend {
    promotionId: number;
    promotionName: string;
    promotionStartDate?: string;
    fixedSpendValues: number[];
}

export interface AccountTableData {
    [key: string]: any;
    currentMonth?: number;
    periodNames?: string[];
    baseUnits?: number[];
    promoUnits?: number[];
    fixedSpendPromotions?: PromotionFixedSpend[];
}

export const copyAccountTableToClipboard = async (
    data: AccountTableData,
    startMonth: number,
    selectedYear?: number,
    currentYear?: number,
    aggregationPeriod?: string,
    showFixedSpendDetails?: boolean,
    showActuals?: boolean,
    ytgSourceData?: AccountTableData
): Promise<void> => {
    if (!data) return;

    // Use period names from API response if available, otherwise fall back to month names.
    // Mirror table behavior: include YTG only when forecast is not fully in the past.
    const basePeriodNames = data.periodNames || getOrderedMonthNames(startMonth);
    const periodNamesOnly = basePeriodNames.filter((name: string) => name !== 'YTG' && name !== 'FY');
    const effectiveCurrentYear = currentYear ?? new Date().getFullYear();
    const effectiveCurrentCalendarMonth = new Date().getMonth() + 1;
    const totalPeriods = aggregationPeriod === 'monthly' ? 12 : periodNamesOnly.length;
    const includeYTG = !isForecastInPast(
        selectedYear,
        effectiveCurrentYear,
        data.currentMonth,
        totalPeriods,
        startMonth,
        effectiveCurrentCalendarMonth
    );
    const periodNames = includeYTG ? [...periodNamesOnly, 'YTG', 'FY'] : [...periodNamesOnly, 'FY'];
    
    let csvContent = '';
    csvContent = addWeekNumberRow(csvContent, periodNames, aggregationPeriod, 1);
    csvContent += '\t' + periodNames.map((name: string) => name.toUpperCase()).join('\t') + '\n';

    // Calculate number of periods (excluding YTG and FY)
    const periodCount = periodNamesOnly.length;
    
    // For FY index, check if we have actual data arrays, otherwise use the period count
    const sampleDataArray = data.baseUnits || data.grossRevenue || Object.values(data).find(Array.isArray) || [];
    const fyIndex = sampleDataArray.length > 0 ? sampleDataArray.length - 1 : periodCount;

    const ytgData = ytgSourceData || data;
    const ytgAggregationPeriod = ytgSourceData && aggregationPeriod !== 'weekly' ? 'weekly' : aggregationPeriod;
    const ytgPeriodNamesOnly = (ytgData.periodNames || basePeriodNames).filter((name: string) => name !== 'YTG' && name !== 'FY');
    const ytgCurrentPeriod = ytgData.currentMonth ?? data.currentMonth;
    const isActualWindowIndex = (index: number): boolean => isPeriodInActualsWindow(index, data.currentMonth);
    const formatAccountPeriodValue = (
        key: string,
        forecastValue: number | undefined,
        actualValue: number | null | undefined,
        periodIndex: number
    ): string => {
        if (!showActuals) {
            return formatValueForClipboard(forecastValue);
        }
        if ((key === 'baseUnits' || key === 'promoUnits') && isActualWindowIndex(periodIndex)) {
            return '-';
        }
        if (actualValue !== null && actualValue !== undefined) {
            return formatValueForClipboard(actualValue);
        }
        if (isActualWindowIndex(periodIndex)) {
            return '';
        }
        return formatValueForClipboard(forecastValue);
    };

    // Add Total Units row first (calculated from baseUnits + promoUnits)
    if (data.baseUnits && data.promoUnits) {
        const promoUnits = data.promoUnits;
        const totalUnits = data.baseUnits.map((baseUnit: number, index: number) => baseUnit + (promoUnits[index] || 0));
        const ytgTotalUnits = ytgData.baseUnits && ytgData.promoUnits
            ? ytgData.baseUnits.map((baseUnit: number, index: number) => baseUnit + ((ytgData.promoUnits as number[])[index] || 0))
            : totalUnits;
        const actualTotalUnits = showActuals ? data.actualTotalUnits : undefined;
        
        const values = totalUnits.slice(0, periodCount).map((value: number, index: number) => {
            const actualValue = actualTotalUnits?.[index];
            return formatAccountPeriodValue('totalUnits', value, actualValue, index);
        });
        const ytgValueWithCurrent = formatValueForClipboard(calculateYTG(ytgTotalUnits, ytgCurrentPeriod, selectedYear, currentYear, ytgAggregationPeriod, startMonth, effectiveCurrentCalendarMonth, true, ytgPeriodNamesOnly));
        const fyForecast = totalUnits[fyIndex];
        const fyActual = actualTotalUnits?.[fyIndex];
        const fyValue = formatValueForClipboard(getValueForCopy(fyForecast, fyActual, showActuals || false));
        csvContent += 'Total Units\t' + values.join('\t') + (includeYTG ? '\t' + ytgValueWithCurrent : '') + '\t' + fyValue + '\n';
    }

    // Add other data rows
    const getActualArrayForAccountRow = (key: string): number[] | undefined => {
        if (!showActuals) {
            return undefined;
        }
        if (key === 'grossRevenue') {
            return data.actualGrossRevenue;
        }
        if (key === 'triggers') {
            return data.actualTriggers;
        }
        if (key === 'overlay') {
            return data.actualOverlay;
        }
        if (key === 'fixedSpend') {
            return data.actualFixedSpend;
        }
        if (key === 'cogs') {
            return data.actualCogs;
        }
        if (key === 'logs') {
            return data.actualLogs;
        }
        if (key === 'netRevenue') {
            return data.actualNetRevenue;
        }
        if (key === 'grossProfit') {
            return data.actualGrossProfit;
        }
        if (key === 'grossProfitPercent') {
            return data.actualGrossProfitPercent;
        }
        return undefined;
    };

    accountRowOrder.forEach((key) => {
        if (!Array.isArray(data[key])) return;

        const rowName = formatAccountRowLabel(key);
        
        const actualArray = getActualArrayForAccountRow(key);
        
        const values = data[key].slice(0, periodCount).map((value: number, index: number) => {
            const actualValue = actualArray?.[index];
            return formatAccountPeriodValue(key, value, actualValue, index);
        });
        const ytgSeries = Array.isArray(ytgData[key]) ? ytgData[key] : data[key];
        const ytgValue = formatValueForClipboard(calculateYTG(ytgSeries, ytgCurrentPeriod, selectedYear, currentYear, ytgAggregationPeriod, startMonth, effectiveCurrentCalendarMonth, true, ytgPeriodNamesOnly));
        const fyForecast = data[key][fyIndex];
        const fyActual = actualArray?.[fyIndex];
        const fyValue = formatValueForClipboard(getValueForCopy(fyForecast, fyActual, showActuals || false));
        csvContent += rowName + '\t' + values.join('\t') + (includeYTG ? '\t' + ytgValue : '') + '\t' + fyValue + '\n';
        
        if (key === 'fixedSpend' && showFixedSpendDetails && data.fixedSpendPromotions) {
            data.fixedSpendPromotions.forEach((promo: PromotionFixedSpend) => {
                const promoValues = promo.fixedSpendValues.slice(0, periodCount).map((value: number) => formatValueForClipboard(value));
                const ytgPromo = ytgData.fixedSpendPromotions?.find((candidate: PromotionFixedSpend) => candidate.promotionId === promo.promotionId);
                const promoYtgValues = ytgPromo?.fixedSpendValues || promo.fixedSpendValues;
                const promoYtgValue = formatValueForClipboard(calculateYTG(promoYtgValues, ytgCurrentPeriod, selectedYear, currentYear, ytgAggregationPeriod, startMonth, effectiveCurrentCalendarMonth, true, ytgPeriodNamesOnly));
                const promoFyIndex = promo.fixedSpendValues.length > 0 ? promo.fixedSpendValues.length - 1 : periodCount;
                const promoFyValue = formatValueForClipboard(promo.fixedSpendValues[promoFyIndex]);
                csvContent += promo.promotionName + '\t' + promoValues.join('\t') + (includeYTG ? '\t' + promoYtgValue : '') + '\t' + promoFyValue + '\n';
            });
        }
    });

    try {
        await navigator.clipboard.writeText(csvContent);
    } catch (err) {
        console.error('Failed to copy: ', err);
    }
};

